import React from 'react';

import isObject from 'lodash/isObject';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import {
  FormContextType,
  GenericObjectType,
  ObjectFieldTemplateProps,
  ObjectFieldTemplatePropertyType,
  RJSFSchema,
  StrictRJSFSchema,
  UiSchema,
  canExpand,
  descriptionId,
  getTemplate,
  getUiOptions,
  titleId,
} from '@rjsf/utils';
import { Row, Col } from 'antd';
import { isRootId } from './ids';

const DESCRIPTION_COL_STYLE = {
  paddingBottom: '8px',
};

/* Nesting is drawn, not measured. Each object hangs its fields behind a rule
   that starts under its own name and runs to the bottom of the group, so the
   levels are told apart by the lines they sit behind and the depth follows
   from the markup being recursive.

   This replaced an indent computed from the number of underscores in the
   field's generated id, capped at three levels — a schema nested four deep
   drew `retention` level with the `schedule` containing it. */

/** The `ObjectFieldTemplate` is the template to use to render all the inner properties of an object along with the
 * title and description if available. If the object is expandable, then an `AddButton` is also rendered after all
 * the properties.
 *
 * @param props - The `ObjectFieldTemplateProps` for this component
 */
export default function CustomObjectFieldTemplate<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any
>(props: ObjectFieldTemplateProps<T, S, F>) {
  const {
    description,
    disabled,
    formContext,
    formData,
    idSchema,
    onAddClick,
    properties,
    readonly,
    required,
    registry,
    schema,
    title,
    uiSchema,
  } = props;

  const uiOptions = getUiOptions<T, S, F>(uiSchema);
  const TitleFieldTemplate = getTemplate<'TitleFieldTemplate', T, S, F>('TitleFieldTemplate', registry, uiOptions);
  const DescriptionFieldTemplate = getTemplate<'DescriptionFieldTemplate', T, S, F>(
    'DescriptionFieldTemplate',
    registry,
    uiOptions
  );
  // Button templates are not overridden in the uiSchema
  const {
    ButtonTemplates: { AddButton },
  } = registry.templates;
  /* Gutter 0, not 24: an antd Row with a gutter carries -12px side margins, so
     every row of the form sat 24px wider than the pane holding it and the
     right-hand edge of each control was clipped. The fields space themselves
     now (`.sb-field`), so the row does not need to. */
  const { colSpan = 24, rowGutter = 0 } = formContext as GenericObjectType;

  const findSchema = (element: ObjectFieldTemplatePropertyType): S => element.content.props.schema;

  const findSchemaType = (element: ObjectFieldTemplatePropertyType) => findSchema(element).type;

  const findUiSchema = (element: ObjectFieldTemplatePropertyType): UiSchema<T, S, F> | undefined =>
    element.content.props.uiSchema;

  const findUiSchemaField = (element: ObjectFieldTemplatePropertyType) => getUiOptions(findUiSchema(element)).field;

  const findUiSchemaWidget = (element: ObjectFieldTemplatePropertyType) => getUiOptions(findUiSchema(element)).widget;

  const calculateColSpan = (element: ObjectFieldTemplatePropertyType) => {
    const type = findSchemaType(element);
    const field = findUiSchemaField(element);
    const widget = findUiSchemaWidget(element);

    const defaultColSpan =
      properties.length < 2 || // Single or no field in object.
      type === 'object' ||
      type === 'array' ||
      widget === 'textarea'
        ? 24
        : 12;

    if (isObject(colSpan)) {
      const colSpanObj: GenericObjectType = colSpan;
      if (isString(widget)) {
        return colSpanObj[widget];
      }
      if (isString(field)) {
        return colSpanObj[field];
      }
      if (isString(type)) {
        return colSpanObj[type];
      }
    }
    if (isNumber(colSpan)) {
      return colSpan;
    }
    return defaultColSpan;
  };

  const isRoot = isRootId(idSchema.$id);

  return (
    <fieldset id={idSchema.$id} className={`sb-obj${isRoot ? ' sb-obj--root' : ''}`}>
      {/* The name of the group, and what it is for, outside the rule — the
          rule hangs from them.

          A group's name is a heading, not a form label. It used to borrow
          antd's `ant-form-item-label` class, which is right-aligned — so every
          section title sat against the far edge of the pane, away from the
          fields it names. */}
      {title && (
        <div className="sb-obj__head">
          <TitleFieldTemplate
            id={titleId<T>(idSchema)}
            title={title}
            required={required}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
        </div>
      )}
      {description && (
        <div style={DESCRIPTION_COL_STYLE}>
          <DescriptionFieldTemplate
            id={descriptionId<T>(idSchema)}
            description={description}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
        </div>
      )}

      <div className="sb-obj__body">
        <Row gutter={rowGutter}>
          {properties
            .filter((e) => !e.hidden)
            .map((element: ObjectFieldTemplatePropertyType) => (
              <Col key={element.name} span={calculateColSpan(element)}>
                {element.content}
              </Col>
            ))}
        </Row>
      </div>

      {canExpand(schema, uiSchema, formData) && (
        <Col span={24}>
          <Row gutter={rowGutter} justify='end'>
            <Col flex='192px'>
              <AddButton
                className='object-property-expand'
                disabled={disabled || readonly}
                onClick={onAddClick(schema)}
                uiSchema={uiSchema}
                registry={registry}
              />
            </Col>
          </Row>
        </Col>
      )}
    </fieldset>
  );
}